import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useUpdateOrderRuleStatus, {
  OrderRuleStatus,
} from "@/features/line-sheet-sets/hooks/use-update-order-rule-status";

// 라디오에 노출할 상태. PENDING(대기로 되돌리기)은 이미 처리한 세트에서만 의미가 있어
// 현재 상태가 PENDING 이 아닐 때만 보여준다.
const STATUS_OPTIONS: { value: OrderRuleStatus; key: string }[] = [
  { value: "APPLIED", key: "applied" },
  { value: "SKIPPED", key: "skipped" },
  { value: "NO_ORDER_RULE", key: "no_order_rule" },
  { value: "PENDING", key: "pending" },
];

export interface OrderRuleStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  agencyId: number;
  lineSheetSetId: number;
  currentStatus?: OrderRuleStatus;
  mutate?: () => void;
}

export default function OrderRuleStatusModal({
  isOpen,
  onClose,
  agencyId,
  lineSheetSetId,
  currentStatus,
  mutate,
}: OrderRuleStatusModalProps) {
  const { t, tTitle } = useI18nHelper();
  const toasts = useAppToasts();
  const { fire, isLoading } = useUpdateOrderRuleStatus();
  const [status, setStatus] = useState<OrderRuleStatus>("APPLIED");

  // 열 때마다 기본값(적용 완료)으로 되돌린다.
  useEffect(() => {
    if (isOpen) {
      setStatus("APPLIED");
    }
  }, [isOpen]);

  const options = STATUS_OPTIONS.filter(
    (option) => option.value !== "PENDING" || currentStatus !== "PENDING"
  );

  const handleSubmit = () => {
    if (isLoading) {
      return;
    }
    fire({ agencyId, lineSheetSetId }, { status }, { rethrow: true })
      .then(() => {
        toasts.info(t("common:order_rule_status.messages.updated"));
        mutate?.();
        onClose();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tTitle("common:order_rule_status.update")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <RadioGroup
            value={status}
            onChange={(value) => setStatus(value as OrderRuleStatus)}
          >
            <Flex flexDirection={"column"} gap={"12px"}>
              {options.map((option) => (
                <Radio key={option.value} value={option.value}>
                  <Flex flexDirection={"column"}>
                    <Text textStyle={"body-md-regular"}>
                      {tTitle(`common:order_rule_status.${option.key}`)}
                    </Text>
                    <Text textStyle={"body-sm-regular"} color={"#6F6F6C"}>
                      {t(`common:order_rule_status.${option.key}.help`)}
                    </Text>
                  </Flex>
                </Radio>
              ))}
            </Flex>
          </RadioGroup>
        </ModalBody>
        <ModalFooter gap={"8px"}>
          <AppButton variant={"gray"} onClick={onClose}>
            {tTitle("cancel")}
          </AppButton>
          <AppButton
            variant={"primary"}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {tTitle("continue")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
